import { useMemo } from "react"
import type { SimEngine } from "../order-sim/engine"
import { changeRow, deleteThread, getQueue, listThreads, postTurn, removeQueued, setMode } from "./api"
import { DRAFTING_NOW, SECTIONS, glance, rerunLine, verdict } from "./decisionModel"
import { useDocsStore } from "./docs.store"
import { DraftPaper, useDraftExport, useDraftSections } from "./DraftPane"
import { sectionLabel } from "./sections"
import { useSimStore } from "./store"
import "./oio-sim.css"

/** The Order-in-Original simulator on the shared run-through pages (order-sim). */
export const oioEngine: SimEngine = {
  kind: "oio",
  base: "/oio-sim",
  app: "oio_sim",
  short: "OIO",
  orderName: "Order-in-Original",
  untitled: "OIO draft",
  sections: SECTIONS,
  draftingNow: DRAFTING_NOW,
  verdict,
  glance,
  rerunLine,
  callStage: "findings",
  calls: [["confirm", "Confirm"], ["reduce", "Reduce"], ["drop", "Drop"]],
  callClass: { drop: "for", reduce: "mixed", confirm: "against" },
  intent: true,
  amountStage: "alignment",
  pauseNext: {
    intake: "Continue to allegations", alignment: "Continue to threshold", threshold: "Continue to findings",
    findings: "Continue to quantum", quantum: "Continue to the draft",
  },
  // The finding sections and the Order say most about a change; name those first.
  rankSection: (id) => (id.startsWith("findings.") ? 0 : id === "order" ? 1 : 2),
  sectionWord: (id) => (id.startsWith("findings.") ? id.slice(9) : id === "order" ? "the Order" : id),
  margin: (sectionId, stages) => {
    const out = []
    if (sectionId.startsWith("findings.")) {
      const row = stages.findings?.rows.find((r) => r.id === sectionId.slice(9))
      if (row) out.push({ from: "04", head: `${row.id} ${CALL_WORD[row.call ?? ""] ?? row.call ?? ""}`.trim(), anchor: `nsd-row-findings-${row.id}`, action: "change the call" })
    }
    if (sectionId === "threshold") {
      const row = stages.threshold?.rows.find((r) => r.id === "section")
      if (row) out.push({ from: "03", head: `${row.label ?? ""} ${row.text}`.trim(), anchor: "nsd-threshold", action: "change it" })
    }
    if (sectionId === "order") {
      const row = stages.quantum?.rows.find((r) => r.id === "total")
      if (row) out.push({ from: "05", head: `${row.label ?? ""} ${row.text}`.trim(), anchor: "nsd-quantum", action: "see the quantum" })
    }
    return out
  },
  passedLine: "Passed · figures match 05 · nothing flagged.",

  useThread: (threadId) => useSimStore((s) => s.threads[threadId]),
  hydrate: (threadId, signal) => useSimStore.getState().hydrate(threadId, signal),
  useDocs: (threadId) => {
    const docs = useDocsStore((s) => s.threads[threadId]?.docs)
    return useMemo(
      () => Object.values(docs ?? {}).filter((d) => d.status !== "failed").map((d) => ({ docId: d.docId, filename: d.filename, status: d.status })),
      [docs],
    )
  },
  hydrateDocs: (threadId, signal) => useDocsStore.getState().hydrate(threadId, signal),
  uploadDoc: (threadId, file) => useDocsStore.getState().upload(threadId, file),
  api: { changeRow, deleteThread, listThreads, postTurn, setMode, getQueue: async (id: string) => getQueue(id), removeQueued: async (id: string, rid: string) => removeQueued(id, rid) },
  useDraftExport,
  useDraftSections,
  DraftPaper,
  sectionLabel,
  sectionAnchor: (id) => `osim-sec-${id}`,
}

const CALL_WORD: Record<string, string> = { confirm: "Confirm", reduce: "Reduce", drop: "Drop" }
